import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { readWorkspace, branchDisplay } from "../utils/workspace";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { DateInput } from "./ui/date-input";

const NewCaseDialog = ({ open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  useEffect(() => { axios.get(`${API}/branches`).then(r => setBranches((r.data?.branches || []).map(b => b.label))).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    gak_number: "",
    gak_year: new Date().getFullYear().toString(),
    eak_number: "",
    filing_date: new Date().toISOString().split("T")[0],
    hearing_date: "",
    court_branch: readWorkspace(user)?.branch || "Πρωτοδικείο Αθηνών",
    court_type: "Μονομελές",
    defendant_residence: "domestic",
    is_state_party: false,
    subject: "",
    plaintiffs: "",
    defendants: "",
    procedure_type: ""
  });

  const handleSave = async () => {
    if (!form.gak_number || !form.gak_year || !form.filing_date) {
      toast.error("Συμπληρώστε τα υποχρεωτικά πεδία (ΓΑΚ, Έτος, Ημερομηνία)");
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/cases`, form);
      toast.success("Η υπόθεση δημιουργήθηκε επιτυχώς");
      onSuccess?.();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα κατά τη δημιουργία");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      gak_number: "",
      gak_year: new Date().getFullYear().toString(),
      eak_number: "",
      filing_date: new Date().toISOString().split("T")[0],
      hearing_date: "",
      court_branch: readWorkspace(user)?.branch || "Πρωτοδικείο Αθηνών",
      court_type: "Μονομελές",
      defendant_residence: "domestic",
      is_state_party: false,
      subject: "",
      plaintiffs: "",
      defendants: "",
      procedure_type: ""
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Νέα Υπόθεση</DialogTitle>
          <DialogDescription>
            Καταχωρήστε νέα υπόθεση στο σύστημα
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>ΓΑΚ Αριθμός *</Label>
              <Input
                value={form.gak_number}
                onChange={(e) => setForm({...form, gak_number: e.target.value})}
                placeholder="π.χ. 12345"
                data-testid="new-case-gak"
              />
            </div>
            <div className="space-y-2">
              <Label>ΓΑΚ Έτος *</Label>
              <Input
                value={form.gak_year}
                onChange={(e) => setForm({...form, gak_year: e.target.value})}
                placeholder="π.χ. 2026"
                data-testid="new-case-year"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>ΕΑΚ</Label>
              <Input
                value={form.eak_number}
                onChange={(e) => setForm({...form, eak_number: e.target.value})}
                placeholder="Προαιρετικό"
              />
            </div>
            <div className="space-y-2">
              <Label>Ημ. Κατάθεσης *</Label>
              <DateInput
                
                value={form.filing_date}
                onChange={(e) => setForm({...form, filing_date: e.target.value})}
                data-testid="new-case-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ημ. Δικασίμου</Label>
              <DateInput
                
                value={form.hearing_date}
                onChange={(e) => setForm({...form, hearing_date: e.target.value})}
                data-testid="new-case-hearing-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Διαδικασία</Label>
              <Input
                value={form.procedure_type}
                onChange={(e) => setForm({...form, procedure_type: e.target.value})}
                placeholder="π.χ. Τακτική"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Κατάστημα</Label>
              <Select value={form.court_branch} onValueChange={(v) => setForm({...form, court_branch: v})}>
                <SelectTrigger data-testid="new-case-court-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {branches.map(b => <SelectItem key={b} value={b}>{branchDisplay(b)}</SelectItem>)}
                  {!branches.includes(form.court_branch) && <SelectItem value={form.court_branch}>{branchDisplay(form.court_branch)}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Τύπος Δικαστηρίου</Label>
              <Select 
                value={form.court_type} 
                onValueChange={(value) => setForm({...form, court_type: value})}
              >
                <SelectTrigger data-testid="new-case-court-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                  <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Κατοικία Εναγομένου</Label>
              <Select 
                value={form.defendant_residence} 
                onValueChange={(value) => setForm({...form, defendant_residence: value})}
              >
                <SelectTrigger data-testid="new-case-residence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">Εσωτερικό (30 ημ.)</SelectItem>
                  <SelectItem value="abroad">Εξωτερικό (30 ημ.)</SelectItem>
                  <SelectItem value="unknown">Αγνώστου Διαμονής (30 ημ.)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Πεδίο για Δημόσιο */}
          <div className="space-y-2">
            <Label>Διάδικος το Δημόσιο;</Label>
            <Select 
              value={form.is_state_party ? "yes" : "no"} 
              onValueChange={(value) => setForm({...form, is_state_party: value === "yes"})}
            >
              <SelectTrigger data-testid="new-case-state-party">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Όχι</SelectItem>
                <SelectItem value="yes">Ναι (εξαίρεση 1/7-15/9)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Αντικείμενο</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({...form, subject: e.target.value})}
              placeholder="Αντικείμενο υπόθεσης"
              data-testid="new-case-subject"
            />
          </div>

          <div className="space-y-2">
            <Label>Ενάγοντες/Αιτούντες</Label>
            <Input
              value={form.plaintiffs}
              onChange={(e) => setForm({...form, plaintiffs: e.target.value})}
              placeholder="Στοιχεία εναγόντων"
              data-testid="new-case-plaintiffs"
            />
          </div>

          <div className="space-y-2">
            <Label>Εναγόμενοι</Label>
            <Input
              value={form.defendants}
              onChange={(e) => setForm({...form, defendants: e.target.value})}
              placeholder="Στοιχεία εναγομένων"
              data-testid="new-case-defendants"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Ακύρωση
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="submit-new-case-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Αποθήκευση...
              </>
            ) : (
              "Δημιουργία"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewCaseDialog;
